// src/pages/TopRestaurants.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Filter, ChevronLeft, ChevronRight } from 'lucide-react';

const TopRestaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('rank');
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    setIsLoading(true);
    setTimeout(() => {
      setRestaurants(getMockTopRestaurants());
      setIsLoading(false);
    }, 800);
  }, []);

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  const getMockTopRestaurants = () => {
    return [
      {
        id: 1,
        name: "Le Petit Bistro",
        image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2070",
        cuisine: "French",
        price: "$$$",
        rating: 4.8,
        reviews: 324,
        location: "75002, Paris",
        features: ["Outdoor Seating", "Halal Certified"],
        rank: 1
      },
      {
        id: 2,
        name: "Chez Ahmed",
        image: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2074",
        cuisine: "Middle Eastern",
        price: "$$",
        rating: 4.7,
        reviews: 187,
        location: "75019, Paris",
        features: ["Halal Certified", "Family-Friendly"],
        rank: 2
      },
      {
        id: 3,
        name: "Sushi Yoko",
        image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?q=80&w=2070",
        cuisine: "Japanese",
        price: "$$$$",
        rating: 4.9,
        reviews: 156,
        location: "75008, Paris",
        features: ["Halal Options"],
        rank: 3
      },
      {
        id: 4,
        name: "La Méditerranée",
        image: "https://images.unsplash.com/photo-1600891964599-f61f4298bdbc?q=80&w=2070",
        cuisine: "Mediterranean",
        price: "$$$",
        rating: 4.5,
        reviews: 203,
        location: "75006, Paris",
        features: ["Halal Options", "Outdoor Seating"],
        rank: 4
      },
      {
        id: 5,
        name: "Pasta Paradiso",
        image: "https://images.unsplash.com/photo-1579684288361-5c1a2957cc38?q=80&w=2071",
        cuisine: "Italian",
        price: "$$",
        rating: 4.6,
        reviews: 184,
        location: "75001, Paris",
        features: ["Halal Options"],
        rank: 5
      },
      {
        id: 6,
        name: "Le Café Parisien",
        image: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?q=80&w=2047",
        cuisine: "French",
        price: "$$",
        rating: 4.4,
        reviews: 222,
        location: "75004, Paris",
        features: ["Outdoor Seating"],
        rank: 6
      },
      {
        id: 7,
        name: "Wrap'N Roll",
        image: "https://images.unsplash.com/photo-1553621042-f6e147245754?q=80&w=2025",
        cuisine: "Japanese",
        price: "$",
        rating: 4.3,
        reviews: 129,
        location: "75009, Paris",
        features: ["Halal Certified", "Takeout Available"],
        rank: 7
      },
      {
        id: 8,
        name: "Bon Amis",
        image: "https://images.unsplash.com/photo-1579617487912-9ecc0bb85e99?q=80&w=2069",
        cuisine: "French",
        price: "$$$",
        rating: 4.7,
        reviews: 178,
        location: "75014, Paris",
        features: ["Wine List", "Reservations Recommended"],
        rank: 8
      },
      {
        id: 9,
        name: "Al-Madina Grill",
        image: "https://images.unsplash.com/photo-1606787366850-de6330128bfc?q=80&w=2070",
        cuisine: "Middle Eastern",
        price: "$$",
        rating: 4.5,
        reviews: 120,
        location: "75010, Paris",
        features: ["Halal Certified", "Family-Friendly"],
        rank: 9
      },
      {
        id: 10,
        name: "Gusti Gusto",
        image: "https://images.unsplash.com/photo-1560611588-163f295eb145?q=80&w=2070",
        cuisine: "Italian",
        price: "$$",
        rating: 4.5,
        reviews: 165,
        location: "75017, Paris",
        features: ["Halal Options", "Vegetarian Options"],
        rank: 10
      },
      {
        id: 11,
        name: "Terry's Café",
        image: "https://images.unsplash.com/photo-1521017432531-fbd92d768814?q=80&w=2070",
        cuisine: "French",
        price: "$$",
        rating: 4.2,
        reviews: 98,
        location: "75015, Paris",
        features: ["Outdoor Seating"],
        rank: 11
      },
      {
        id: 12,
        name: "Les Rupins",
        image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070",
        cuisine: "French",
        price: "$$",
        rating: 4.4,
        reviews: 132,
        location: "75010, Paris",
        features: ["Wine List"],
        rank: 12
      }
    ];
  };

  const filterRestaurants = () => {
    let filtered = [...restaurants];
    
    if (activeFilter !== 'all') {
      filtered = filtered.filter(restaurant => 
        restaurant.cuisine.toLowerCase() === activeFilter.toLowerCase()
      );
    }

    if (sortBy === 'rating') {
      filtered.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'reviews') {
      filtered.sort((a, b) => b.reviews - a.reviews);
    } else if (sortBy === 'rank') {
      filtered.sort((a, b) => a.rank - b.rank);
    }

    return filtered;
  };

  const getCuisineTypes = () => {
    const cuisines = new Set(restaurants.map(restaurant => restaurant.cuisine));
    return ['all', ...Array.from(cuisines)];
  };

  if (isLoading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading top restaurants...</p>
      </div>
    );
  }

  return (
    <div className="top-restaurants-page py-5">
      <div className="container">
        {/* Header Section */}
        <div className="mb-5 text-center">
          <h1 className="fw-bold mb-3">Top 100 Restaurants in Paris</h1>
          <p className="lead text-muted">Discover the best dining experiences based on millions of reviews</p>
        </div>

        {/* Mobile Filters */}
        <div className="d-md-none mb-4">
          <div className="position-relative">
            <div className="d-flex align-items-center mb-3">
              <h5 className="m-0">Filter by Cuisine</h5>
              <div className="ms-auto">
                <button onClick={scrollLeft} className="btn btn-sm btn-light me-1">
                  <ChevronLeft size={16} />
                </button>
                <button onClick={scrollRight} className="btn btn-sm btn-light">
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
            <div 
              ref={scrollContainerRef}
              className="d-flex flex-nowrap overflow-auto pb-2 hide-scrollbar"
              style={{ scrollBehavior: 'smooth' }}
            >
              {getCuisineTypes().map(cuisine => (
                <button
                  key={cuisine}
                  className={`btn ${activeFilter === cuisine ? 'btn-primary' : 'btn-outline-secondary'} me-2 flex-shrink-0`}
                  onClick={() => setActiveFilter(cuisine)}
                >
                  {cuisine === 'all' ? 'All Cuisines' : cuisine}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex align-items-center">
              <label className="me-2"><Filter size={16} className="me-1" /> Sort by:</label>
              <select 
                className="form-select" 
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="rank">Top Ranking</option>
                <option value="rating">Highest Rating</option>
                <option value="reviews">Most Reviews</option>
              </select>
            </div>
          </div>
        </div>

        {/* Desktop Filters and Sorting */}
        <div className="d-none d-md-block">
          <div className="row mb-4 g-3">
            <div className="col-lg-7">
              <div className="d-flex flex-wrap gap-2">
                {getCuisineTypes().map(cuisine => (
                  <button
                    key={cuisine}
                    className={`btn ${activeFilter === cuisine ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => setActiveFilter(cuisine)}
                  >
                    {cuisine === 'all' ? 'All Cuisines' : cuisine}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-lg-5">
              <div className="d-flex align-items-center justify-content-lg-end">
                <label className="me-2"><Filter size={18} className="me-1" /> Sort by:</label>
                <select 
                  className="form-select w-auto" 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="rank">Top Ranking</option>
                  <option value="rating">Highest Rating</option>
                  <option value="reviews">Most Reviews</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Restaurant List */}
        <div className="row g-4">
          {filterRestaurants().map(restaurant => (
            <div key={restaurant.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
              <div className="card h-100 border-0 shadow-sm hover-elevation">
                <div className="position-relative">
                  <img
                    src={restaurant.image}
                    className="card-img-top"
                    alt={restaurant.name}
                    style={{ height: '180px', objectFit: 'cover' }}
                    onError={(e) => {
                      e.target.onerror = null; 
                      e.target.src = 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?q=80&w=2070';
                    }}
                  />
                  <div className="position-absolute top-0 start-0 bg-primary text-white p-2 m-2 rounded-circle"
                       style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {restaurant.rank}
                  </div>
                  <div className="position-absolute top-0 end-0 m-2">
                    <div className="bg-dark bg-opacity-75 text-white px-2 py-1 rounded d-flex align-items-center">
                      <Star className="text-warning" size={14} />
                      <span className="ms-1 fw-bold">{restaurant.rating}</span>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <h5 className="card-title fw-bold mb-1">{restaurant.name}</h5>
                  <div className="d-flex align-items-center mb-2">
                    <span className="badge bg-light text-dark me-2">{restaurant.cuisine}</span>
                    <span className="text-muted small">{restaurant.price}</span>
                    <span className="text-muted small ms-auto">{restaurant.reviews} reviews</span>
                  </div>
                  <p className="text-muted small mb-2">
                    <MapPin size={14} className="me-1" />
                    {restaurant.location}
                  </p>
                  <div className="mb-3">
                    {restaurant.features.map((feature, index) => (
                      <span key={index} className="badge bg-light text-dark me-1 mb-1">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="card-footer bg-white border-top-0 py-3">
                  <Link to={`/restaurants/${restaurant.id}`} className="btn btn-outline-primary w-100">
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {/* <div className="d-flex justify-content-center mt-5">
          <nav aria-label="Page navigation">
            <ul className="pagination">
              <li className="page-item"><a className="page-link" href="#">Previous</a></li>
              <li className="page-item active"><a className="page-link" href="#">1</a></li>
              <li className="page-item"><a className="page-link" href="#">2</a></li>
              <li className="page-item"><a className="page-link" href="#">3</a></li>
              <li className="page-item"><a className="page-link" href="#">Next</a></li>
            </ul>
          </nav>
        </div> */}
      </div>

      {/* CSS for hover effect and hiding scrollbar */}
      <style jsx>{`
        .hover-elevation {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .hover-elevation:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default TopRestaurants;